/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// redux
import { useSelector, useDispatch } from "react-redux";
// react router
import { useHistory } from "react-router-dom";

// node.js library that concatenates classes (strings)
import classnames from "classnames";
// javascipt plugin for creating charts
import Chart from "chart.js";
// react plugin used to create charts
import { Line, Bar, Doughnut } from "react-chartjs-2";
// reactstrap components
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  NavItem,
  NavLink,
  Nav,
  Container,
  Row,
  Col,
  UncontrolledTooltip,
  Modal,
  Table,
} from "reactstrap";
// react plugin used to create DropdownMenu for selecting items
import Select2 from "react-select2-wrapper";
// QR code generator
import QRCode from 'qrcode.react';
// react component used to create sweet alerts
import ReactBSAlert from "react-bootstrap-sweetalert";
// Print
import { useReactToPrint } from 'react-to-print';

// core components
import CardsHeader from "components/Headers/CardsHeader.js";
import Loading from "components/Misc/Loading";

import {
  chartOptions,
  parseOptions,
  chartExample1,
  chartExample7,
} from "variables/charts.js";

// filter actions
import { setTimeFrame } from "actions/filterActions";
// checklists actions
import { getPerformanceData, createDataStructure, setLimitReached } from 'actions/checklistActions';

function Dashboard() {
  const userState = useSelector(state => state.user);
  const checklistsState = useSelector(state => state.checklists);
  const filterState = useSelector(state => state.filter);
  const dispatch = useDispatch();
  const history = useHistory();
  const [activeNav, setActiveNav] = React.useState(filterState.timeFrame === 'week' ? 1 : filterState.timeFrame === 'month' ? 2 : 3);
  const [chartExample1Data, setChartExample1Data] = React.useState("data1");
  const [modal, setModal] = React.useState(false);
  const [modalFeedback, setModalFeedback] = React.useState(false);
  const isMounted = React.useRef(false);
  const [alert, setAlert] = React.useState(false);

  const [focusedFranchiseSelect, setFocusedFranchiseSelect] = React.useState(
    false
  );
  const [focusedLocationSelect, setFocusedLocationSelect] = React.useState(
    false
  );
  const [franchiseSelect, setFranchiseSelect] = React.useState();
  const [locationSelect, setLocationSelect] = React.useState();

  const qrCodeRef = React.useRef();

  const pieOptions = {
    responsive: true,
    legend: {
      position: "top",
    },
    animation: {
      animateScale: true,
      animateRotate: true,
    },
    onClick: (event, array) => setModal(true)
  };

  const handlePrint = useReactToPrint({
    content: () => qrCodeRef.current,
  })

  const toggleNavs = (e, index) => {
    e.preventDefault();
    setActiveNav(index);
    if (index === 1) {
      dispatch(setTimeFrame());
    } else if (index === 2) {
      dispatch(setTimeFrame("month"));
    } else {
      dispatch(setTimeFrame("year"));
    }
    setChartExample1Data(chartExample1Data === "data1" ? "data2" : "data1");
  };
  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  React.useEffect(async () => {
    try {
      if (!isMounted.current) {
        isMounted.current = true;
        return;
      }
      if (checklistsState.limitReached) {
        return dispatch(createDataStructure());
      }
      else if (filterState.timeFrame === 'week' && checklistsState.performance && checklistsState.checklistsCompletedChart.labels.length !== 7) {
        return dispatch(createDataStructure());
      }
      else if (filterState.timeFrame === 'month' && checklistsState.performance && checklistsState.checklistsCompletedChart.labels.length !== 4) {
        return dispatch(createDataStructure());
      }
      else if (filterState.timeFrame === 'year' && checklistsState.performance8 && checklistsState.checklistsCompletedChart.labels.length !== 12) {
        return dispatch(createDataStructure());
      } else {
        await dispatch(getPerformanceData());
        dispatch(createDataStructure());
      }
      if (filterState.timeFrame === 'month' && checklistsState.performance) {
        dispatch(setLimitReached());
      };
      if (filterState.timeFrame === 'year' && checklistsState.performance) {
        dispatch(setLimitReached());
      };
    } catch(error) {
      console.error(error)
    }
  }, [filterState.timeFrame])

  React.useEffect(() => {
    setFranchiseSelect(filterState.filter.franchise);
    setLocationSelect(filterState.filter.address);
  }, [filterState.filter.franchise, filterState.filter.address]);

  if (
    !checklistsState.checklistsCompletedChart ||
    !checklistsState.checklistsOnTimeChart ||
    !checklistsState.checklistsPassedChart ||
    checklistsState.isFetching
  ) {
    return <Loading active={true} />;
  }
  return (
    <>
      {alert}
      <CardsHeader name="Dashboard" parentName="Dashboards" />
      <Container className="mt--6" fluid>
        <Modal
          isOpen={modalFeedback}
          toggle={() => {
            setModalFeedback(false);
          }}
          className="modal-dialog-centered modal-secondary"
          style={{ width: "90%", maxWidth: "1200px" }}
        >
          <div className="modal-body">
            <Row>
              <div className="col">
                <Card className="shadow px-6">
                  <CardHeader className="bg-transparent border-0">
                    <h3 className="mb-0">Generate Feedback QR code</h3>
                  </CardHeader>
                  <Form
                    className="edit-event--form pt-3"
                    id="form"
                  >
                    <FormGroup>
                      <label
                        className="text-muted"
                        htmlFor="exampleFormControlSelect2"
                      >
                        Select the franchises you want the Feedback QR code for.
                      </label>
                      <Select2
                        className="form-control"
                        data-minimum-results-for-search="Infinity"
                        data={Object.keys(checklistsState.franchises)}
                        // style={validFranchiseSelect === "valid" ? {borderColor: '#2dce89'} : validFranchiseSelect === "invalid" ? {borderColor: '#fb6340'} : {}}
                        onFocus={() => setFocusedFranchiseSelect(true)}
                        onBlur={() => setFocusedFranchiseSelect(false)}
                        onSelect={e => {
                          setFranchiseSelect(e.params.data.text)
                        }}
                        value={franchiseSelect}
                      />
                    </FormGroup>
                    {franchiseSelect ? (
                        <FormGroup>
                          <label
                            className="text-muted"
                            htmlFor="exampleFormControlSelect2"
                          >
                            Select the location.
                          </label>
                          <Select2
                            className="form-control"
                            data-minimum-results-for-search="Infinity"
                            data={Object.keys(checklistsState.franchises)
                                .map(franchise => {
                                  if (!franchiseSelect.includes(franchise))
                                    return null;
                                  return [
                                    ...checklistsState.franchises[
                                      franchise
                                    ].locations.map(
                                      location => location.address
                                    ),
                                  ];
                                })
                                .flat()
                                .filter(item => item)
                            }
                            // style={validFranchiseSelect === "valid" ? {borderColor: '#2dce89'} : validFranchiseSelect === "invalid" ? {borderColor: '#fb6340'} : {}}
                            onFocus={() => setFocusedLocationSelect(true)}
                            onBlur={() => setFocusedLocationSelect(false)}
                            onSelect={e => {
                              setLocationSelect(e.params.data.text)
                            }}
                            value={locationSelect}
                          />
                        </FormGroup>
                      ) : null
                    }
                  </Form>
                </Card>
              </div>
            </Row>
          </div>
          <div className="modal-footer">
            <Button color="primary" type="button" onClick={() => {
              setAlert(
                <ReactBSAlert
                  info
                  showCancel
                  style={{ display: "block", marginTop: "-100px" }}
                  title={`${franchiseSelect} | ${locationSelect}`}
                  onConfirm={() => {
                    handlePrint();
                    setAlert(null);
                  }}
                  onCancel={() => setAlert(null)}
                  confirmBtnBsStyle="warning"
                  confirmBtnText="Print Now"
                  cancelBtnBsStyle="primary"
                  cancelBtnText="Print Later"
                  btnSize=""
                >
                  <p>This is the QR code for this location's feedback. Please select if you want to print it now or later.</p>
                  <div ref={qrCodeRef} className="d-flex justify-content-center align-items-center" style={{ width: '100%', height: '300px' }}>
                    <QRCode value={encodeURI(`${window.location.origin}/auth/feedback?location=${franchiseSelect}|${locationSelect}`)}/>
                  </div>
                </ReactBSAlert>
              );
            }} disabled={!franchiseSelect || !locationSelect}>
              Generate QR code
            </Button>
            <Button color="secondary" onClick={() => {
              setModalFeedback(false);
            }}>
              Close
            </Button>
          </div>
        </Modal>
        <Modal
          isOpen={modal}
          toggle={() => setModal(false)}
          className="modal-dialog-centered modal-secondary"
          style={{ width: "80%", maxWidth: "1000px" }}
        >
          <div className="modal-body">
            <Table
              className="align-items-center table-dark table-flush"
              responsive
            >
              <thead className="thead-dark">
                <tr>
                <th scope="col">
                    <div className="d-flex flex-column justify-content-center align-items-center">
                      Feedback
                    </div>
                  </th>
                  <th scope="col">
                    <div className="d-flex flex-column justify-content-center align-items-center">
                      Medium Satisfaction
                      <small>Total reviews (% of all reviews)</small>
                    </div>
                  </th>
                  <th scope="col">
                    <div className="d-flex flex-column justify-content-center align-items-center">
                      Low Satisfaction
                      <small>Total reviews (% of all reviews)</small>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="list">
                {
                  Object.keys(checklistsState.statistics.additionalFeedback.medium).map(feedback => (
                    <tr key={feedback}>
                      <th scope="row">
                        <span className="mb-0 text-sm">
                          {feedback}
                        </span>
                      </th>
                      <td>
                        <span className="d-flex justify-content-center">
                          {checklistsState.statistics.additionalFeedback.medium[feedback]}
                          <small className="ml-2">
                          {`(${Math.round((checklistsState.statistics.additionalFeedback.medium[feedback] / checklistsState.statistics.additionalFeedback.totalFeedback) * 1000) / 10}%)`}
                          </small>
                        </span>
                      </td>
                      <td>
                      <span className="d-flex justify-content-center">
                          {checklistsState.statistics.additionalFeedback.low[feedback]}
                          <small className="ml-2">
                          {`(${Math.round((checklistsState.statistics.additionalFeedback.low[feedback] / checklistsState.statistics.additionalFeedback.totalFeedback) * 1000) / 10}%)`}
                          </small>
                        </span>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </Table>
          </div>
          <div className="modal-footer">
            <Button
              className="ml-auto"
              color="secondary"
              onClick={() => setModal(false)}
            >
              Close
            </Button>
          </div>
        </Modal>
        <Row>
          <Col xl="6">
            <Card className="bg-default">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    {/* <h6 className="text-light text-uppercase ls-1 mb-1">
                      Overview
                    </h6> */}
                    <h5 className="h3 text-white mb-0">Checklists Completed</h5>
                  </div>
                  <div className="col">
                    <Nav className="justify-content-end flex-nowrap" pills>
                      <NavItem className="mr-2 mr-md-0">
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 1,
                          })}
                          href="#weekly"
                          onClick={e => toggleNavs(e, 1)}
                        >
                          <span className="d-none d-md-block">Week</span>
                          <span className="d-md-none">Y</span>
                        </NavLink>
                      </NavItem>
                      <NavItem className="mr-2 mr-md-0">
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 2,
                          })}
                          href="#monthly"
                          onClick={e => toggleNavs(e, 2)}
                        >
                          <span className="d-none d-md-block">Month</span>
                          <span className="d-md-none">M</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 3,
                          })}
                          data-toggle="tab"
                          href="#yearly"
                          onClick={e => toggleNavs(e, 3)}
                        >
                          <span className="d-none d-md-block">Year</span>
                          <span className="d-md-none">W</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  <Line
                    data={checklistsState.checklistsCompletedChart}
                    options={chartExample1.options}
                    id="chart-sales-dark"
                    className="chart-canvas"
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="6">
            <Card className="bg-default">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    {/* <h6 className="text-light text-uppercase ls-1 mb-1">
                      Overview
                    </h6> */}
                    <h5 className="h3 text-white mb-0">
                      Completed on Time VS Completed Late
                    </h5>
                  </div>
                  <div className="col">
                    <Nav className="justify-content-end flex-nowrap" pills>
                      <NavItem className="mr-2 mr-md-0">
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 1,
                          })}
                          href="#weekly"
                          onClick={e => toggleNavs(e, 1)}
                        >
                          <span className="d-none d-md-block">Week</span>
                          <span className="d-md-none">Y</span>
                        </NavLink>
                      </NavItem>
                      <NavItem className="mr-2 mr-md-0">
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 2,
                          })}
                          href="#monthly"
                          onClick={e => toggleNavs(e, 2)}
                        >
                          <span className="d-none d-md-block">Month</span>
                          <span className="d-md-none">M</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 3,
                          })}
                          data-toggle="tab"
                          href="#yearly"
                          onClick={e => toggleNavs(e, 3)}
                        >
                          <span className="d-none d-md-block">Year</span>
                          <span className="d-md-none">W</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  <Bar
                    data={checklistsState.checklistsOnTimeChart}
                    options={chartExample7.options}
                    className="chart-canvas"
                    id="chart-bar-stacked"
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xl="6">
            <Card className="bg-default" style={{ minHeight: '100%' }}>
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col d-flex align-items-center">
                    {/* <h6 className="text-light text-uppercase ls-1 mb-1">
                      Overview
                    </h6> */}
                    <h5 className="h3 text-white mb-0">
                      Customer Satisfaction
                    </h5>
                    {
                      userState.user.role === 'owner' && userState.user.plan === 'free' ?
                      (
                        <Badge color="primary" pill className="ml-2">
                          Pro
                        </Badge>
                      ) :
                      (
                        <div className="rounded-circle d-flex align-items-center justify-content-center">
                          <i className="fas fa-info-circle text-primary ml-2" id="password" />
                          <UncontrolledTooltip
                            delay={0}
                            placement="top"
                            target="password"
                          >
                            Click anywhere on the chart to see feedback details.
                          </UncontrolledTooltip>
                        </div>
                      )
                    }
                  </div>
                  <div className="col">
                  {
                    (userState.user.role !== 'owner' || userState.user.plan !== 'free') &&
                    <Nav className="justify-content-end flex-nowrap" pills>
                      <NavItem className="mr-2 mr-md-0">
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 1,
                          })}
                          href="#weekly"
                          onClick={e => toggleNavs(e, 1)}
                        >
                          <span className="d-none d-md-block">Week</span>
                          <span className="d-md-none">W</span>
                        </NavLink>
                      </NavItem>
                      <NavItem className="mr-2 mr-md-0">
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 2,
                          })}
                          href="#monthly"
                          onClick={e => toggleNavs(e, 2)}
                        >
                          <span className="d-none d-md-block">Month</span>
                          <span className="d-md-none">M</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 3,
                          })}
                          data-toggle="tab"
                          href="#yearly"
                          onClick={e => toggleNavs(e, 3)}
                        >
                          <span className="d-none d-md-block">Year</span>
                          <span className="d-md-none">Y</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  }
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                {
                  userState.user.role === 'owner' && userState.user.plan === 'free' ?
                  (
                    <div className="d-flex flex-column justify-content-center" style={{ minHeight: '100%', height: '100%' }}>
                      <p className="text-white mt-5">
                        Generate and print a QR code for your customers to scan and provide feedback. See their satisfaction index charted out, and their feedback so that you know what can be improved!
                      </p>
                      <Button
                        className="mt-5"
                        color="primary"
                        onClick={e => history.push("/admin/billing")}
                      >
                        Upgrade to Pro
                      </Button>
                    </div>
                  ) :
                  (
                    <>
                      <div className="chart">
                        <Doughnut
                          data={checklistsState.customerSatisfactionChart}
                          options={pieOptions}
                          className="chart-canvas"
                          id="chart-doughnut"
                        />
                      </div>
                      <div style={{position: 'absolute', bottom: '0', right: '0'}} className="d-flex justify-content-end">
                        {/* <Button color="primary" className="mb-3 mr-3 d-none d-xl-block">
                          Feedback QR
                        </Button> */}
                        <Button color="primary" className="mb-3 mr-3" size='sm' onClick={() => setModalFeedback(true)}>
                          Feedback QR
                        </Button>
                      </div>
                    </>
                  )
                }
              </CardBody>
            </Card>
          </Col>
          <Col xl="6">
            <Card className="bg-default">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    {/* <h6 className="text-light text-uppercase ls-1 mb-1">
                      Overview
                    </h6> */}
                    <h5 className="h3 text-white mb-0">Checklists Passed</h5>
                  </div>
                  <div className="col">
                    <Nav className="justify-content-end flex-nowrap" pills>
                      <NavItem className="mr-2 mr-md-0">
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 1,
                          })}
                          href="#weekly"
                          onClick={e => toggleNavs(e, 1)}
                        >
                          <span className="d-none d-md-block">Week</span>
                          <span className="d-md-none">Y</span>
                        </NavLink>
                      </NavItem>
                      <NavItem className="mr-2 mr-md-0">
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 2,
                          })}
                          href="#monthly"
                          onClick={e => toggleNavs(e, 2)}
                        >
                          <span className="d-none d-md-block">Month</span>
                          <span className="d-md-none">M</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", {
                            active: activeNav === 3,
                          })}
                          data-toggle="tab"
                          href="#yearly"
                          onClick={e => toggleNavs(e, 3)}
                        >
                          <span className="d-none d-md-block">Year</span>
                          <span className="d-md-none">W</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  <Line
                    data={checklistsState.checklistsPassedChart}
                    options={chartExample1.options}
                    id="chart-sales-dark"
                    className="chart-canvas"
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Dashboard;
